import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import UnlockBlock from './components/UnlockOption';
import {
  getEffectiveConditionsV2,
  toUIFormat,
  fromUIFormat,
  hasValidTaskId,
  createEmptyBlock,
  createEmptyTaskEntry,
  toConditionEntry,
} from './utils/conditionHelpers';
import { DEFAULT_OPTIONS } from '../../FlowchartView/TaskNode/DecisionOptionsModal';
import { boardTasksLive } from '../../BoardView/boardTasksLive';

import './styles/modal.css';
import './styles/header.css';
import './styles/footer.css';

/**
 * ConditionsDropdown Component
 * Modal for configuring unlock conditions with nested AND/OR block logic.
 * 
 * Internal state:
 *   blocks: [{ conditions: [{ taskId, branch, waitSeconds? }], innerOperator: 'AND'|'OR' }, ...]
 *   outerOperators: [null, 'AND'|'OR', ...]
 */
const ConditionsDropdown = ({
  task,
  allTasks = [],
  onConditionsChange,
  isOpen,
  onClose,
}) => {
  const dropdownRef = useRef(null);

  const [blocks, setBlocks] = useState(() => {
    const v2 = getEffectiveConditionsV2(task);
    const ui = toUIFormat(v2);
    return ui.blocks.length > 0 ? ui.blocks : [];
  });

  const [outerOperators, setOuterOperators] = useState(() => {
    const v2 = getEffectiveConditionsV2(task);
    const ui = toUIFormat(v2);
    return ui.outerOperators.length > 0 ? ui.outerOperators : [];
  });

  const taskSource = boardTasksLive.current || allTasks;
  const availableTasks = useMemo(() => {
    const isMilestoneEditor = task?.nodeType === 'milestone';
    return taskSource.filter((t) => {
      if (t.id === task.id) return false;
      if (isMilestoneEditor) {
        return t.nodeType !== 'milestone' && t.nodeType !== 'note';
      }
      return t.nodeType !== 'milestone' && t.nodeType !== 'note';
    });
  }, [taskSource, task.id, task?.nodeType]);

  const saveConditions = useCallback((newBlocks, newOuterOps) => {
    const validBlocks = newBlocks
      .map(b => ({
        conditions: b.conditions.filter(c => hasValidTaskId(c)),
        innerOperator: b.innerOperator || 'AND',
      }))
      .filter(b => b.conditions.length > 0);

    const validOuterOps = newOuterOps.slice(0, validBlocks.length);

    const v2 = fromUIFormat(validBlocks, validOuterOps);
    onConditionsChange?.(task.id, v2);
  }, [task.id, onConditionsChange]);

  const getDecisionOptions = useCallback((taskObj) => {
    if (taskObj?.nodeType !== 'decision') return [];
    return taskObj?.options?.length > 0 ? taskObj.options : DEFAULT_OPTIONS;
  }, []);

  // Auto-set branch for decision nodes that have branch: null
  const fixDecisionBranches = useCallback((inputBlocks) => {
    let changed = false;
    const fixed = inputBlocks.map(block => ({
      ...block,
      conditions: block.conditions.map(cond => {
        if (!cond.taskId || cond.branch) return cond;
        const t = availableTasks.find(at => at.id === cond.taskId);
        const opts = getDecisionOptions(t);
        if (opts.length > 0) {
          changed = true;
          return { ...cond, branch: opts[0].id };
        }
        return cond;
      }),
    }));
    return { blocks: fixed, changed };
  }, [availableTasks, getDecisionOptions]);

  // Sync state when task changes
  useEffect(() => {
    const v2 = getEffectiveConditionsV2(task);
    const ui = toUIFormat(v2);
    if (ui.blocks.length > 0) {
      const { blocks: fixedBlocks, changed } = fixDecisionBranches(ui.blocks);
      setBlocks(fixedBlocks);
      setOuterOperators(ui.outerOperators);
      if (changed) saveConditions(fixedBlocks, ui.outerOperators);
    } else if (isOpen) {
      setBlocks([createEmptyBlock()]);
      setOuterOperators([null]);
    } else {
      setBlocks([]);
      setOuterOperators([]);
    }
  }, [task.id, task.unlockConditions, task.dependsOn, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && blocks.length === 0) {
      setBlocks([createEmptyBlock()]);
      setOuterOperators([null]);
    }
  }, [isOpen, blocks.length]);

  // ─── Block-level handlers ───

  const handleAddBlock = () => {
    setBlocks(prev => [...prev, createEmptyBlock()]);
    setOuterOperators(prev => [...prev, 'AND']);
  };

  const handleRemoveBlock = (blockIndex) => {
    const newBlocks = blocks.filter((_, i) => i !== blockIndex);
    const newOps = outerOperators.filter((_, i) => i !== blockIndex);
    if (newOps.length > 0) newOps[0] = null;

    if (newBlocks.length > 0) {
      setBlocks(newBlocks);
      setOuterOperators(newOps);
      saveConditions(newBlocks, newOps);
    } else {
      setBlocks([createEmptyBlock()]);
      setOuterOperators([null]);
      saveConditions([], []);
    }
  };

  const handleToggleOuterOperator = (blockIndex) => {
    const newOps = [...outerOperators];
    newOps[blockIndex] = newOps[blockIndex] === 'AND' ? 'OR' : 'AND';
    setOuterOperators(newOps);
    saveConditions(blocks, newOps);
  };

  const handleToggleInnerOperator = (blockIndex) => {
    const newBlocks = blocks.map((block, i) => {
      if (i !== blockIndex) return block;
      return { ...block, innerOperator: block.innerOperator === 'AND' ? 'OR' : 'AND' };
    });
    setBlocks(newBlocks);
    saveConditions(newBlocks, outerOperators);
  };

  // ─── Condition-level handlers ───

  const handleUpdateTask = (blockIndex, condIndex, newTaskId) => {
    const newBlocks = blocks.map((block, bi) => {
      if (bi !== blockIndex) return block;
      const newConditions = [...block.conditions];
      const old = newConditions[condIndex];
      const oldTaskId = typeof old === 'string' ? old : old?.taskId;

      let branch = null;
      if (oldTaskId === newTaskId) {
        branch = old?.branch || null;
      } else {
        const selectedTask = availableTasks.find(t => t.id === newTaskId);
        const opts = getDecisionOptions(selectedTask);
        if (opts.length > 0) {
          branch = opts[0].id;
        }
      }

      newConditions[condIndex] = toConditionEntry({
        ...(typeof old === 'object' && old ? old : {}),
        taskId: newTaskId,
        branch,
      });
      return { ...block, conditions: newConditions };
    });
    setBlocks(newBlocks);
    saveConditions(newBlocks, outerOperators);
  };

  const handleUpdateBranch = (blockIndex, condIndex, newBranch) => {
    const newBlocks = blocks.map((block, bi) => {
      if (bi !== blockIndex) return block;
      const newConditions = [...block.conditions];
      const old = newConditions[condIndex];
      const taskId = typeof old === 'string' ? old : old?.taskId;
      newConditions[condIndex] = toConditionEntry({
        ...(typeof old === 'object' && old ? old : {}),
        taskId,
        branch: newBranch,
      });
      return { ...block, conditions: newConditions };
    });
    setBlocks(newBlocks);
    saveConditions(newBlocks, outerOperators);
  };

  const handleRemoveCondition = (blockIndex, condIndex) => {
    const newBlocks = blocks.map((block, bi) => {
      if (bi !== blockIndex) return block;
      return { ...block, conditions: block.conditions.filter((_, ci) => ci !== condIndex) };
    }).filter(b => b.conditions.length > 0);

    const newOps = outerOperators.slice(0, newBlocks.length);
    if (newOps.length > 0) newOps[0] = null;

    if (newBlocks.length > 0) {
      setBlocks(newBlocks);
      setOuterOperators(newOps);
    } else {
      setBlocks([createEmptyBlock()]);
      setOuterOperators([null]);
    }
    saveConditions(newBlocks, newOps);
  };

  const handleUpdateWait = (blockIndex, condIndex, waitSeconds) => {
    const newBlocks = blocks.map((block, bi) => {
      if (bi !== blockIndex) return block;
      const newConditions = [...block.conditions];
      const old = newConditions[condIndex];
      const taskId = typeof old === 'string' ? old : old?.taskId;
      const branch = typeof old === 'string' ? null : (old?.branch || null);
      newConditions[condIndex] = toConditionEntry({
        ...(typeof old === 'object' && old ? old : {}),
        taskId,
        branch,
        waitSeconds,
        waitMinutes: undefined,
      });
      return { ...block, conditions: newConditions };
    });
    setBlocks(newBlocks);
    saveConditions(newBlocks, outerOperators);
  };

  const handleAddCondition = (blockIndex) => {
    const newBlocks = blocks.map((block, bi) => {
      if (bi !== blockIndex) return block;
      return { ...block, conditions: [...block.conditions, createEmptyTaskEntry()] };
    });
    setBlocks(newBlocks);
  };

  const handleClearAll = () => {
    setBlocks([createEmptyBlock()]);
    setOuterOperators([null]);
    saveConditions([], []);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const validTaskCount = blocks.reduce(
    (sum, b) => sum + b.conditions.filter(c => hasValidTaskId(c)).length, 0
  );

  const lastBlockHasValidTask = blocks.length > 0 &&
    blocks[blocks.length - 1].conditions.some(c => hasValidTaskId(c));

  if (!isOpen) return null;

  return createPortal(
    <div className="conditions-modal__backdrop" onClick={handleBackdropClick}>
      <div
        ref={dropdownRef}
        className="conditions-modal__container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="conditions-modal__noise" />

        {/* Header */}
        <div className="conditions-modal__header">
          <div className="conditions-modal__icon-wrapper">
            <div className="conditions-modal__orbit" />
            <div className="conditions-modal__particle" />
            <div className="conditions-modal__particle conditions-modal__particle--secondary" />
            <div className="conditions-modal__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M9 15l2 2 4-4" />
              </svg>
            </div>
          </div>
          <p className="conditions-modal__pretitle">
            Before you can start working on:
          </p>
          <h3 className="conditions-modal__title">
            {task.name}
          </h3>
          <p className="conditions-modal__subtitle">
            ...the below conditions must be satisfied:
          </p>
        </div>

        {/* Body */}
        <div className="conditions-modal__body">
          <div className="conditions-modal__options">
            {blocks.map((block, blockIndex) => (
              <UnlockBlock
                key={blockIndex}
                block={block}
                blockIndex={blockIndex}
                blockNumber={blockIndex + 1}
                isFirst={blockIndex === 0}
                outerOperator={outerOperators[blockIndex]}
                allTasks={availableTasks}
                onUpdateTask={handleUpdateTask}
                onUpdateBranch={handleUpdateBranch}
                onUpdateWait={handleUpdateWait}
                onRemoveCondition={handleRemoveCondition}
                onAddCondition={handleAddCondition}
                onRemoveBlock={handleRemoveBlock}
                onToggleOuterOperator={handleToggleOuterOperator}
                onToggleInnerOperator={handleToggleInnerOperator}
              />
            ))}
          </div>

          {lastBlockHasValidTask && (
            <button
              className="conditions-modal__add-option"
              onClick={handleAddBlock}
              type="button"
            >
              + Add another block
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="conditions-modal__footer">
          {validTaskCount > 0 && (
            <button
              className="conditions-modal__btn conditions-modal__btn--cancel"
              onClick={handleClearAll}
              type="button"
            >
              Clear
            </button>
          )}
          <button
            className="conditions-modal__btn conditions-modal__btn--primary"
            onClick={onClose}
            type="button"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConditionsDropdown;
